import { type ComputedRef, inject, type InjectionKey } from "vue";

/**
 * Field context provided by FormField and injected by design-system controls
 * (BaseInput, BaseTextarea, BaseSelect, ...) so the wrapper owns the label,
 * hint/error messaging and all aria wiring while the control wires itself.
 */
export interface FormFieldContext {
    /** The id the slotted control should render, matched by the FormField label's `for`. */
    controlId: string;
    /**
     * The id of the FormField's label. A group control (a radio group) is not a labelable element,
     * so `for` cannot name it; it takes this as its aria-labelledby instead.
     */
    labelId: string;
    /** Space-joined ids of the rendered hint/error paragraphs; undefined when neither renders. */
    describedBy: ComputedRef<string | undefined>;
    /** Whether the field currently has an error message. */
    invalid: ComputedRef<boolean>;
    /**
     * The id, aria-describedby and aria-invalid bindings above as one object, so a control
     * wires itself with a single `v-bind="field?.controlAttrs.value"`. Keys are omitted when
     * they have no value, and bare usage (no FormField) binds nothing.
     */
    controlAttrs: ComputedRef<FormFieldControlAttrs>;
}

export interface FormFieldControlAttrs {
    id: string;
    "aria-describedby"?: string;
    "aria-invalid"?: "true";
}

export const formFieldContextKey: InjectionKey<FormFieldContext> = Symbol("formFieldContext");

/** Returns the surrounding FormField's context, or null when the control is used bare. */
export function useFormField(): FormFieldContext | null {
    return inject(formFieldContextKey, null);
}
